import {
  HHAlert,
  HHButton,
  HHCircularProgress,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import { OpenInNew } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useDynamicDialogContext } from '../../context/dynamic-dialog.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import {
  GetSubscriptionsDocument,
  useBulkVoidSubscriptionsMutation,
  useGetBillsBySubscriptionIdQuery,
} from '../../types';
import { buildBillManagementUrl } from '../bills/bill-management/bill-management-navigator';
import { BillSource } from '../bills/bill-types';
import { GridLevelName } from '../grid-components/grid-types';
import {
  isNoteValid,
  noteInvalid,
  tooLong,
} from '../grid-components/panel-validation';
import LoadingComponent from '../utils/loading';

interface VoidSubscriptionDialogProps {
  subscriptionId: number;
  // Invoked after the subscription is successfully voided, so the caller can
  // tear down stale UI (e.g. close the subscription details drawer).
  onSuccess?: () => void;
}

export const VoidSubscriptionDialog = ({
  subscriptionId,
  onSuccess,
}: VoidSubscriptionDialogProps): JSX.Element => {
  const { closeDialog, setDialogDismissible } = useDynamicDialogContext();
  const { setSnackbarState } = useSnackbarContext();
  const [note, setNote] = useState<string>('');
  const [rechecking, setRechecking] = useState<boolean>(false);

  const [bulkVoidSubscriptions, { loading: voiding }] =
    useBulkVoidSubscriptionsMutation();

  const {
    data: billsData,
    loading: billsLoading,
    refetch: refetchBills,
  } = useGetBillsBySubscriptionIdQuery({
    variables: {
      source: BillSource.SUBSCRIPTIONS,
      sourceId: subscriptionId,
    },
    fetchPolicy: 'cache-and-network',
  });

  // A subscription cannot be voided while any of its bills has a submission that
  // is not voided — those bills must be cleared in submission management first.
  // (A submission with status "void" no longer blocks.)
  const blockedBillIds = (billsData?.getBillsBySubscriptionId ?? [])
    .filter(bill =>
      (bill.submissions ?? []).some(submission => submission.status !== 'void'),
    )
    .map(bill => bill.id);
  const hasBlockingSubmissions = blockedBillIds.length > 0;
  const checkingBills = billsLoading && !billsData;

  // Safety net: always re-enable user close if this dialog unmounts mid-flight.
  useEffect(() => () => setDialogDismissible(true), [setDialogDismissible]);

  const openSubmissions = (): void => {
    const url = buildBillManagementUrl(
      { billIds: blockedBillIds },
      GridLevelName.SUBMISSION,
    );
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleRecheck = async (): Promise<void> => {
    setRechecking(true);
    try {
      await refetchBills();
    } finally {
      setRechecking(false);
    }
  };

  const handleVoid = async (): Promise<void> => {
    // Lock the dialog so the user cannot close it while the request is running.
    setDialogDismissible(false);
    try {
      const result = await bulkVoidSubscriptions({
        variables: {
          bulkVoidSubscriptionsInput: {
            ids: [subscriptionId],
            note,
          },
        },
        refetchQueries: [GetSubscriptionsDocument],
      });

      if ((result.data?.bulkVoidSubscriptions?.length ?? 0) === 0) {
        setSnackbarState(
          `Subscription ${subscriptionId} was not voided`,
          'error',
          5000,
        );
        setDialogDismissible(true);
        return;
      }

      setSnackbarState(
        `Successfully voided subscription ${subscriptionId}`,
        'success',
        5000,
      );
      closeDialog();
      onSuccess?.();
    } catch (e) {
      setSnackbarState(
        `Failed to void subscription with error: ${
          e instanceof Error ? e.message : 'Unknown error'
        }`,
        'error',
        5000,
      );
      setDialogDismissible(true);
    }
  };

  const voidDisabled =
    voiding ||
    rechecking ||
    checkingBills ||
    hasBlockingSubmissions ||
    noteInvalid(note);

  return (
    <HHStack direction="column" spacing={4}>
      {checkingBills ? (
        <LoadingComponent center helperText="Checking attached bills…" />
      ) : hasBlockingSubmissions ? (
        <HHAlert
          severity="error"
          title="Cannot void subscription"
          hhVariant="outlined"
        >
          <HHStack spacing={2}>
            <HHTypography hhVariant="body">
              {blockedBillIds.length === 1
                ? '1 bill attached to this subscription has a submission'
                : `${blockedBillIds.length} bills attached to this subscription have submissions`}{' '}
              and must be cleared in submission management before it can be
              voided.
            </HHTypography>
            <HHStack direction="row">
              <HHButton
                hhVariant="variant-bypass"
                variant="outlined"
                color="error"
                endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                onClick={openSubmissions}
                data-testid="void-subscription-view-submissions-button"
              >
                View submissions
              </HHButton>
            </HHStack>
          </HHStack>
        </HHAlert>
      ) : (
        <HHStack spacing={4}>
          <HHAlert
            severity="warning"
            title="Void subscription"
            hhVariant="outlined"
          >
            <HHTypography hhVariant="body">
              Voiding subscription {subscriptionId} cannot be undone. Please
              provide a reason for this action.
            </HHTypography>
          </HHAlert>
          <HHTextField
            multiline
            label="Why is this subscription being voided?"
            hhVariant="variant-bypass"
            variant="outlined"
            value={note}
            onChange={(e): void => setNote(e.target.value)}
            fullWidth
            rows={5}
            helperText={
              isNoteValid(note)
                ? ''
                : 'Note must be between 5 and 500 characters'
            }
            error={tooLong(note)}
            inputProps={{ role: 'textbox', name: 'note' }}
          />
        </HHStack>
      )}
      <HHStack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="flex-end"
      >
        <HHButton
          hhVariant="variant-bypass"
          variant="outlined"
          color="primary"
          disabled={voiding}
          onClick={closeDialog}
          data-testid="void-subscription-cancel-button"
        >
          Cancel
        </HHButton>
        {hasBlockingSubmissions && (
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            color="primary"
            disabled={rechecking || voiding}
            startIcon={
              rechecking ? (
                <HHCircularProgress size={16} styleAs="primary" />
              ) : undefined
            }
            onClick={(): void => {
              void handleRecheck();
            }}
            data-testid="void-subscription-recheck-button"
          >
            {rechecking ? 'Rechecking…' : 'Recheck'}
          </HHButton>
        )}
        <HHButton
          hhVariant="variant-bypass"
          variant="contained"
          color="error"
          disabled={voidDisabled}
          startIcon={
            voiding ? (
              <HHCircularProgress size={16} styleAs="primary" />
            ) : undefined
          }
          onClick={(): void => {
            void handleVoid();
          }}
          data-testid="void-subscription-confirm-button"
        >
          {voiding ? 'Voiding…' : 'Void'}
        </HHButton>
      </HHStack>
    </HHStack>
  );
};
